import * as React from "react";
import { connect } from "react-redux";
import { StoreState } from "../../reducers/root-reducer";
import { WithStyles, Theme, createStyles, withStyles } from "@material-ui/core";
import { translate, InjectedTranslateProps } from "react-i18next";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Button from "@material-ui/core/Button";
import { Payment } from '../../api/payments';
import { Dispatch } from 'redux';
import { closeAddPaymentDialog } from '../../actions/store/app-actions';
import { refreshInquiry } from '../../actions/inquiry-actions';
import { addHours } from 'date-fns';
import { formatDistanceStrictToNow } from '../../util/date-utility';
import { conf } from '../../conf';
import { addPaymentAndUpdateStore, updatePaymentAndUpdateStore } from '../../actions/payment-actions';

interface State {
	dialogAmount: string;
	dialogItemName: string;
	dialogName: string;
	dialogEmail: string;
	dialogValidHours: number;
	dialogMessage?: string;
}

export interface AddPaymentDialogProps {}

type Props = AddPaymentDialogProps &
	ReturnType<typeof mapStateToProps> &
	ReturnType<typeof mapDispatchToProps> &
	WithStyles<typeof styles> &
	InjectedTranslateProps;

export class AddPaymentDialog extends React.Component<Props, {}> {
	public readonly state: State = {
		dialogAmount: this.props.passedAmount ? this.props.passedAmount : conf.payments.defaultAmount.toFixed(2),
		dialogItemName: "Anzahlung Tischreservierung",
		dialogName: this.props.passedName ? this.props.passedName : "",
		dialogEmail: this.props.passedEmail ? this.props.passedEmail : "",
		dialogValidHours: this.props.passedValidHours ? this.props.passedValidHours : conf.payments.defaultValidHours,
		dialogMessage: this.props.passedMessage ? this.props.passedMessage : ""
	};

	private form: HTMLFormElement;

	private handleFormRef = (ref: HTMLFormElement) => {
		this.form = ref;
	}

	public componentWillReceiveProps(nextProps: Props) {
		if (this.props.open === false && nextProps.open === true) {
			this.setStateFromProps(nextProps);
		}
	}

	private setStateFromProps = (props: Props) => {
		this.setState({
			dialogAmount: props.passedAmount ? props.passedAmount : conf.payments.defaultAmount.toFixed(2),
			dialogName: props.passedName ? props.passedName : "",
			dialogEmail: props.passedEmail ? props.passedEmail : "",
			dialogValidHours: props.passedValidHours ? props.passedValidHours : conf.payments.defaultValidHours,
			dialogMessage: props.passedMessage ? props.passedMessage : ""
		});
	};

	private handleDialogAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const dialogAmount = parseFloat(event.currentTarget.value);

		this.setState({
			dialogAmount: isNaN(dialogAmount) ? "" : dialogAmount
		});
	};

	private handleDialogAmountBlur = () => {
		const { dialogAmount } = this.state;

		const roundedAmount =
			dialogAmount === ""
				? "0"
				: parseFloat(dialogAmount).toFixed(2);

		this.setState({ dialogAmount: roundedAmount });
	};

	private handleDialogEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ dialogEmail: event.currentTarget.value });
	};

	private handleDialogItemNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ dialogItemName: event.currentTarget.value });
	};

	private handleDialogValidHoursChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ dialogValidHours: event.currentTarget.value });
	};

	private handleDialogNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ dialogName: event.currentTarget.value });
	};

	private handleDialogMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ dialogMessage: event.currentTarget.value });
	};

	private handleDialogClose = () => {
		this.props.close();
	};

	private handleDialogSubmitClick = async () => {
		const { dialogAmount, dialogName, dialogEmail, dialogItemName, dialogValidHours, dialogMessage } = this.state;
		const { inquiryId, paymentId } = this.props;

		if (!this.form.reportValidity()) return;

		const amount = parseFloat(dialogAmount);
		const isInquiry = inquiryId && inquiryId !== "";

		const payload: Payment = {
			amount,
			currency: 'EUR',
			name: dialogName,
			items: [{
				amount,
				name: dialogItemName
			}],
			expirationDate: addHours(new Date(), dialogValidHours).toISOString(),
			email: dialogEmail,
		};

		if (isInquiry) {
			payload.inquiryId = inquiryId;
		}

		if (Boolean(dialogMessage)) {
			payload.message = dialogMessage;
		}

		if (paymentId && paymentId !== "") {
			await updatePaymentAndUpdateStore(paymentId, payload);
		} else {
			await addPaymentAndUpdateStore(payload);
		}

		if (isInquiry) {
			await refreshInquiry(inquiryId);
		}

		this.handleDialogClose();
	}

	private generateDurationHelperText = () => {
		const { dialogValidHours } = this.state;

		if (dialogValidHours < 24) return;

		let prefix = "";

		if (dialogValidHours % 24 !== 0) {
			prefix = "~ " + prefix;
		}

		return prefix + formatDistanceStrictToNow(addHours(new Date(), dialogValidHours));
	};

	public render() {
		const {
			classes,
			open,
			inquiryId ,
		} = this.props;
		const {
			dialogAmount,
			dialogName,
			dialogEmail,
			dialogItemName,
			dialogValidHours,
			dialogMessage
		} = this.state;

		return (
			<Dialog
				open={open}
				onClose={this.handleDialogClose}
			>
				<DialogTitle>Neue Zahlung anfordern</DialogTitle>
				<DialogContent>
					<form ref={this.handleFormRef}>
						<div className={classes.formRow}>
							<TextField
								autoFocus
								margin="dense"
								type="number"
								required
								label="Betrag"
								className={classes.textField}
								value={isNaN(parseFloat(dialogAmount)) ? "" : dialogAmount}
								onChange={this.handleDialogAmountChange}
								onBlur={this.handleDialogAmountBlur}
								InputProps={{
									startAdornment: <InputAdornment position="start">€</InputAdornment>
								}}
								inputProps={{
									min: "1",
									step: "0.01"
								}}
							/>
							<TextField
								margin="dense"
								type="number"
								value={dialogValidHours}
								required
								label="Gültigkeit"
								helperText={this.generateDurationHelperText()}
								className={classes.textField}
								onChange={this.handleDialogValidHoursChange}
								InputProps={{
									endAdornment: <InputAdornment position="end">Stunden</InputAdornment>
								}}
								inputProps={{
									min: 12,
									max: 336
								}}
							/>
						</div>
						<TextField
							margin="dense"
							type="text"
							value={dialogItemName}
							fullWidth
							required
							label="Zahlungsbeschreibung"
							onChange={this.handleDialogItemNameChange}
						/>
						<div className={classes.formRow}>
							<TextField
								margin="dense"
								type="text"
								value={dialogName}
								required
								label="Name"
								className={classes.textField}
								onChange={this.handleDialogNameChange}
							/>
							<TextField
								margin="dense"
								type="email"
								value={dialogEmail}
								required
								label="E-Mail Adresse"
								className={classes.textField}
								onChange={this.handleDialogEmailChange}
							/>
						</div>
						<TextField
							margin="dense"
							type="text"
							value={dialogMessage}
							fullWidth
							required
							label="Nachricht"
							multiline={true}
							disabled={Boolean(inquiryId)}
							rows={3}
							onChange={this.handleDialogMessageChange}
						/>
					</form>
				</DialogContent>
				<DialogActions>
					<Button
						className="close-add-payment-dialog-button"
						onClick={this.handleDialogClose}
					>
						Abbrechen
					</Button>
					<Button
						className="submit-payment-button"
						onClick={this.handleDialogSubmitClick}
						color="primary"
					>
						Senden
					</Button>
				</DialogActions>
			</Dialog>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	open: state.addPaymentDialogOpen,
	inquiryId: state.addPaymentDialogInquiryId,
	paymentId: state.addPaymentDialogPaymentId,
	passedEmail: state.addPaymentDialogEmail,
	passedName: state.addPaymentDialogName,
	passedAmount: state.addPaymentDialogAmount,
	passedValidHours: state.addPaymentDialogValidHours,
	passedMessage: state.addPaymentDialogMessage
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	close: () => dispatch(closeAddPaymentDialog())
});

const styles = (theme: Theme) =>
	createStyles({
		formRow: {
			display: "flex",
			margin: -10,

			"@media (max-width: 600px)": {
				display: "block"
			}
		},
		textField: {
			margin: 10,
			width: "calc(50% - 20px)",

			"@media (max-width: 600px)": {
				width: "calc(100% - 20px)"
			}
		},
	});

export const ConnectedAddPaymentDialog = translate("translations")(
	withStyles(styles)(connect(mapStateToProps, mapDispatchToProps)(AddPaymentDialog))
);
